import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Save, AlertTriangle } from 'lucide-react';

import { v3Api } from '../hooks/useApi';
import './invoicing.css';

/**
 * Per-concessionaire invoicing configuration.
 *
 * `enabled` defaults to false on every existing client, so the module stays dark
 * until someone deliberately turns it on. That is intentional: deploying an
 * upgrade must not cause a mall to start issuing numbered tax documents to its
 * tenants.
 */
export default function InvoiceSettings() {
    const { t } = useTranslation();

    const [form, setForm] = useState(null);
    const [fallback, setFallback] = useState({});
    const [slots, setSlots] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            v3Api.invoicing.settings(),
            v3Api.invoicing.chargeSlots().catch(() => null),
        ])
            .then(([res, slotRes]) => {
                setForm(res.invoice_settings || {});
                setFallback({
                    address: res.fallback_address,
                    email: res.fallback_email,
                    name: res.concessionaire_name,
                });
                setSlots(slotRes);
            })
            .catch((e) => toast.error(e.response?.data?.message || t('Failed to load invoice settings')))
            .finally(() => setLoading(false));
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const save = () => {
        const rate = Number(form.tax_rate);
        if (Number.isNaN(rate) || rate < 0 || rate > 100) {
            toast.error(t('Tax rate must be between 0 and 100.'));
            return;
        }
        setSaving(true);
        v3Api.invoicing.updateSettings({
            enabled: !!form.enabled,
            tax_rate: rate,
            tax_label: form.tax_label,
            tax_registration_number: form.tax_registration_number,
            billing_address: form.billing_address,
            billing_email: form.billing_email,
            invoice_prefix: form.invoice_prefix,
            invoice_due_days: Number(form.invoice_due_days) || 30,
            auto_generate: !!form.auto_generate,
            auto_generate_day: Number(form.auto_generate_day) || 1,
            auto_email_on_generate: !!form.auto_email_on_generate,
            footer_note: form.footer_note,
        })
            .then((res) => {
                setForm(res.invoice_settings);
                toast.success(t('Invoice settings saved'));
            })
            .catch((e) => toast.error(e.response?.data?.message || t('Save failed')))
            .finally(() => setSaving(false));
    };

    if (loading || !form) {
        return <div className="v3 v3-inv"><div className="v3-inv-empty">{t('Loading…')}</div></div>;
    }

    return (
        <div className="v3 v3-inv">
            <div className="v3-inv-header">
                <div>
                    <h1 className="v3-inv-title">{t('Invoice Settings')}</h1>
                    <p className="v3-inv-sub">{t('Tax, numbering and issuer details for {{name}}.', { name: fallback.name || '' })}</p>
                </div>
                <div className="v3-inv-actions">
                    <button type="button" className="v3-inv-btn v3-inv-btn-primary" onClick={save} disabled={saving}>
                        <Save size={14} /> {saving ? t('Saving…') : t('Save')}
                    </button>
                </div>
            </div>

            {!form.enabled && (
                <div className="v3-inv-banner v3-inv-banner-warn">
                    <AlertTriangle size={16} />
                    <span>{t('Invoicing is switched off for this client. Generation is refused until it is enabled.')}</span>
                </div>
            )}

            <div className="v3-inv-card">
                <h2 className="v3-inv-card-title">{t('Status')}</h2>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                    <input type="checkbox" checked={!!form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
                    {t('Enable invoicing for this client')}
                </label>
            </div>

            <div className="v3-inv-card">
                <h2 className="v3-inv-card-title">{t('Tax')}</h2>
                <div className="v3-inv-filters">
                    <Field label={t('Tax rate (%)')}>
                        <input
                            className="v3-inv-input"
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={form.tax_rate ?? 5}
                            onChange={(e) => set('tax_rate', e.target.value)}
                        />
                    </Field>
                    <Field label={t('Tax label')}>
                        <input
                            className="v3-inv-input"
                            value={form.tax_label || ''}
                            onChange={(e) => set('tax_label', e.target.value)}
                            placeholder="VAT"
                        />
                    </Field>
                    <Field label={t('Tax registration number (TRN)')} wide>
                        <input
                            className="v3-inv-input"
                            value={form.tax_registration_number || ''}
                            onChange={(e) => set('tax_registration_number', e.target.value)}
                        />
                    </Field>
                </div>
                <p className="v3-inv-basis">
                    {t('Applied to every line. Changing it affects invoices generated from now on — invoices already issued keep the rate they were generated with.')}
                </p>
            </div>

            <div className="v3-inv-card">
                <h2 className="v3-inv-card-title">{t('Numbering and terms')}</h2>
                <div className="v3-inv-filters">
                    <Field label={t('Invoice prefix')}>
                        <input
                            className="v3-inv-input"
                            value={form.invoice_prefix || ''}
                            onChange={(e) => set('invoice_prefix', e.target.value)}
                            placeholder="INV"
                        />
                    </Field>
                    <Field label={t('Payment terms (days)')}>
                        <input
                            className="v3-inv-input"
                            type="number"
                            min="0"
                            value={form.invoice_due_days ?? 30}
                            onChange={(e) => set('invoice_due_days', e.target.value)}
                        />
                    </Field>
                </div>
                <p className="v3-inv-basis">
                    {t('Numbers run {{prefix}}-{{year}}-00001 and restart each calendar year. Gaps are normal: a number is reserved before the invoice is written, so a failure mid-run consumes one. A gap is safe; a duplicate number would not be.', {
                        prefix: form.invoice_prefix || 'INV',
                        year: new Date().getFullYear(),
                    })}
                </p>
            </div>

            <div className="v3-inv-card">
                <h2 className="v3-inv-card-title">{t('Issuer details')}</h2>
                <div className="v3-inv-filters">
                    <Field label={t('Billing address')} wide>
                        <input
                            className="v3-inv-input"
                            value={form.billing_address || ''}
                            onChange={(e) => set('billing_address', e.target.value)}
                            placeholder={fallback.address || ''}
                        />
                    </Field>
                    <Field label={t('Billing email')} wide>
                        <input
                            className="v3-inv-input"
                            value={form.billing_email || ''}
                            onChange={(e) => set('billing_email', e.target.value)}
                            placeholder={fallback.email || ''}
                        />
                    </Field>
                    <Field label={t('Invoice footer note')} wide>
                        <input
                            className="v3-inv-input"
                            value={form.footer_note || ''}
                            onChange={(e) => set('footer_note', e.target.value)}
                        />
                    </Field>
                </div>
                <p className="v3-inv-basis">{t('Blank fields fall back to the client record.')}</p>
            </div>

            <div className="v3-inv-card">
                <h2 className="v3-inv-card-title">{t('Automation')}</h2>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 10 }}>
                    <input
                        type="checkbox"
                        checked={!!form.auto_generate}
                        onChange={(e) => set('auto_generate', e.target.checked)}
                    />
                    {t('Generate last month’s invoices automatically')}
                </label>
                {form.auto_generate && (
                    <div className="v3-inv-filters">
                        <Field label={t('Day of month')}>
                            <input
                                className="v3-inv-input"
                                type="number"
                                min="1"
                                max="28"
                                value={form.auto_generate_day ?? 1}
                                onChange={(e) => set('auto_generate_day', e.target.value)}
                            />
                        </Field>
                    </div>
                )}
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 10 }}>
                    <input
                        type="checkbox"
                        checked={!!form.auto_email_on_generate}
                        onChange={(e) => set('auto_email_on_generate', e.target.checked)}
                    />
                    {t('Email invoices to tenants automatically')}
                </label>
                {form.auto_email_on_generate && (
                    <div className="v3-inv-banner v3-inv-banner-warn" style={{ marginTop: 12, marginBottom: 0 }}>
                        <AlertTriangle size={16} />
                        <span>
                            {t('There is no approval step, so invoices will reach tenants without review. Holdover invoices — where the lease end date has passed — are always excluded and must be sent by hand.')}
                        </span>
                    </div>
                )}
            </div>

            {/* Charge slots are read-only here, but showing them matters: these
                names are what appear as invoice line items, and the binding
                between a slot and a name has been a source of mislabelled
                money. Seeing it explicitly is the point. */}
            {slots?.map && Object.keys(slots.map).length > 0 && (
                <div className="v3-inv-card">
                    <h2 className="v3-inv-card-title">{t('Charge names on invoices')}</h2>
                    <div className="v3-inv-table-wrap">
                        <table className="v3-inv-table">
                            <thead>
                                <tr>
                                    <th>{t('Tenant field')}</th>
                                    <th>{t('Appears on the invoice as')}</th>
                                    <th>{t('Status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(slots.map).sort((a, b) => Number(a) - Number(b)).map((slot) => (
                                    <tr key={slot}>
                                        <td className="v3-inv-mono">tenant_charge{slot}</td>
                                        <td>{slots.map[slot]}</td>
                                        <td className="v3-inv-muted">
                                            {slots.meta?.[slot]?.active === false ? t('Unassigned (still billed if an amount remains)') : t('Active')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="v3-inv-basis">
                        {t('Managed under Configurations → Other Charges. Each invoice freezes this mapping at generation, so renaming a charge never relabels an invoice that has already been issued.')}
                    </p>
                </div>
            )}
        </div>
    );
}

function Field({ label, children, wide }) {
    return (
        <div className="v3-inv-field" style={wide ? { minWidth: 280, flex: 1 } : undefined}>
            <span className="v3-inv-label">{label}</span>
            {children}
        </div>
    );
}
